import { AGENT_ALIASES, AgentId, getAgentAvatarImage } from '@/constants/agentNames'
import type { Workflow } from '../types'
import type { RunListItem } from '../v4/contracts'

/**
 * The workflow library over v4 runs: how one run becomes a library card, the status line the card
 * shows, the chips the library filters by, and the bell's entries. Pure; tested.
 */

type T = (key: string, opts: { defaultValue: string; [k: string]: unknown }) => string

/** The engine's status and outcome in the words the library shows. */
export function statusLine(item: RunListItem, t: T): string {
  switch (item.status) {
    case 'understanding':
      return t('workflow:status.understanding', { defaultValue: 'Reading your question' })
    case 'awaiting_ask':
      return t('workflow:status.awaiting_ask', { defaultValue: 'Waiting for your answer' })
    case 'planning':
      return t('workflow:status.planning', { defaultValue: 'Planning' })
    case 'awaiting_approval':
      return t('workflow:status.awaiting_approval', { defaultValue: 'Waiting for your approval' })
    case 'running':
      return t('workflow:status.running', { defaultValue: 'Running' })
    case 'parked':
      return t('workflow:status.parked', { defaultValue: 'Waiting on you' })
    case 'finished': {
      const finished = t('workflow:status.finished', { defaultValue: 'Finished' })
      const outcome = item.outcome ? outcomeText(item.outcome, t) : ''
      return outcome ? `${finished}: ${outcome}` : finished
    }
    default:
      return String(item.status)
  }
}

function outcomeText(outcome: NonNullable<RunListItem['outcome']>, t: T): string {
  switch (outcome) {
    case 'satisfied':
      return t('workflow:outcome.satisfied', { defaultValue: 'answered' })
    case 'partial':
      return t('workflow:outcome.partial', { defaultValue: 'partly answered' })
    case 'blocked':
      return t('workflow:outcome.blocked', { defaultValue: 'blocked' })
    case 'unsafe':
      return t('workflow:outcome.unsafe', { defaultValue: 'stopped, unsafe' })
    case 'refused':
      return t('workflow:outcome.refused', { defaultValue: 'no procedure fits' })
    case 'cancelled':
      return t('workflow:outcome.cancelled', { defaultValue: 'cancelled' })
    case 'guardrail_stopped':
      return t('workflow:outcome.guardrail_stopped', { defaultValue: 'stopped by a guardrail' })
    default:
      return String(outcome)
  }
}

/** The run waits on the engineer: an ask, an approval, or a parked step. */
export const needsYou = (item: RunListItem): boolean =>
  item.status === 'awaiting_ask' || item.status === 'awaiting_approval' || item.status === 'parked'

/** The run ended badly: the engine could not or would not answer. */
const FAILED_OUTCOMES = new Set<NonNullable<RunListItem['outcome']>>(['blocked', 'unsafe', 'refused', 'guardrail_stopped'])
export const failed = (item: RunListItem): boolean =>
  item.status === 'finished' && !!item.outcome && FAILED_OUTCOMES.has(item.outcome)

/** The library's chips: everything, what waits on you, what the engine is working on, what ended. */
export type RunKind = 'all' | 'needs_you' | 'running' | 'finished'

export function kindOf(item: RunListItem): Exclude<RunKind, 'all'> {
  if (needsYou(item)) return 'needs_you'
  if (item.status === 'finished') return 'finished'
  return 'running'
}

/**
 * One run as a library card. The question is the name; the answer's headline is the description
 * once the run has one, else the status line; the run's last activity is its date (also its
 * "created" for the sorts: the list carries no start time), and the engine's persona its
 * executor, as the cards always showed one.
 */
export function runToCard(item: RunListItem, t: T): Workflow {
  return {
    id: item.runId,
    name: item.question,
    description: item.status === 'finished' && item.headline ? item.headline : statusLine(item, t),
    discipline: null,
    executors: [
      {
        id: AgentId.PLANT_SUPPORT,
        name: AGENT_ALIASES[AgentId.PLANT_SUPPORT].displayName,
        avatarUrl: getAgentAvatarImage(AgentId.PLANT_SUPPORT),
      },
    ],
    createdAt: item.updatedAt,
    lastOpenedAt: item.updatedAt,
    createdByMe: true,
    builtin: false,
  }
}

export interface RunNotification {
  runId: string
  title: string
  subtitle: string
  at: string
}

/** The bell's two lists, newest activity first: runs waiting on the engineer, and runs that ended badly. */
export function buildRunNotifications(items: RunListItem[], t: T): { needsReview: RunNotification[]; failedRuns: RunNotification[] } {
  const sorted = [...items].sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt))
  const entry = (item: RunListItem): RunNotification => ({
    runId: item.runId,
    title: item.question,
    subtitle: statusLine(item, t),
    at: item.updatedAt,
  })
  return {
    needsReview: sorted.filter(needsYou).map(entry),
    failedRuns: sorted.filter(failed).map(entry),
  }
}

/** Free-text search over the question and the status line, then the chosen sort. */
export function selectRuns(
  cards: Workflow[],
  opts: { search: string; sortBy: 'last_used' | 'newest' | 'name_az' | 'name_za' }
): Workflow[] {
  const q = opts.search.trim().toLowerCase()
  const filtered = q
    ? cards.filter((w) => w.name.toLowerCase().includes(q) || w.description.toLowerCase().includes(q))
    : cards
  const ms = (iso: string | null) => (iso ? Date.parse(iso) : 0)
  const sorted = [...filtered]
  switch (opts.sortBy) {
    case 'name_az':
      sorted.sort((a, b) => a.name.localeCompare(b.name))
      break
    case 'name_za':
      sorted.sort((a, b) => b.name.localeCompare(a.name))
      break
    // A run's created and last-activity dates are the same list field: both sorts are by activity.
    case 'newest':
    case 'last_used':
    default:
      sorted.sort((a, b) => ms(b.lastOpenedAt) - ms(a.lastOpenedAt))
  }
  return sorted
}
